import { useMemo } from "react";
import { SimpleTable } from "@/core/components/simple-table";
import { cn } from "@/core/utils/utils";
import { IReferral } from "@/referrals/referral";
import ActionsCol from "./cols/actions-col";
import AdvocateCol from "./cols/advocate-col";
import CustomerNameCol from "./cols/customer-name-col";
import DealSizeCol from "./cols/deal-size-col";
import PaidToDateCol from "./cols/paid-to-date-col";
import ReferredDateCol from "./cols/referred-date-col";
import ServiceCol from "./cols/service-col";
import StatusCol from "./cols/status-col";

interface ReferralTableProps {
  referrals: IReferral[];
  isLoading: boolean;
  searchValue?: string;
  setSearchValue?: (value: string) => void;
  selectedReferral: IReferral | null;
  onRowClick: (referral: IReferral) => void;
  setPayoutReferral: (referral: IReferral) => void;
  onLost: (referralId: string) => void;
  onMoveToPaid: (referralId: string) => void;
  onDelete: (referralId: string) => void;
  className?: string;
  showStatusColumn?: boolean;
}

export const ReferralTable = ({
  referrals,
  isLoading,
  searchValue,
  setSearchValue,
  selectedReferral,
  onRowClick,
  setPayoutReferral,
  onLost,
  onMoveToPaid,
  onDelete,
  className,
  showStatusColumn = true,
}: ReferralTableProps) => {
  const columns = useMemo(() => {
    const baseColumns = [
      CustomerNameCol(),
      ReferredDateCol(),
      ...(showStatusColumn ? [StatusCol()] : []),
      AdvocateCol(),
      DealSizeCol(),
      PaidToDateCol(),
      ServiceCol(),
      ActionsCol({ setPayoutReferral, onLost, onMoveToPaid, onDelete }),
    ];
    return baseColumns;
  }, [setPayoutReferral, onLost, onMoveToPaid, onDelete, showStatusColumn]);

  return (
    <SimpleTable
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      data={referrals}
      columns={columns}
      isLoading={isLoading}
      className={cn("h-full min-h-0 w-full min-w-0 pt-6", className)}
      scrollAreaClassName="pr-0"
      tableClassName="min-w-[1200px]"
      selectedRowId={selectedReferral?.id}
      onRowClick={onRowClick}
      rowDataCy="referral-table-row"
      columnPinning={{
        left: ["customer-name", "referred-date"],
        right: ["actions"],
      }}
    />
  );
};
